// src/curated/hornbill/hornbillData.js
// Hornbill Festival 2026 (Nagaland): festival facts, the do-it-yourself plan, and packages from local
// operators, all on one page (routechef.com/hornbill-festival). Each package also lives in the partner
// hub (backend/curated_trip_agent/hornbill_listings.py: same slugs), so traveller chats reach the operator
// and they can claim their pages. Package details are as each operator lists them; nothing is added.
// Keep the copy free of long dashes.

// Photos (public/trips/hornbill): 1600 px and 800 px, JPG and WebP. focus = what stays in view when cropped.
export const PHOTOS = {
    morung: { src: "/trips/hornbill/hornbill-morung.jpg", src_small: "/trips/hornbill/hornbill-morung-800.jpg", webp: "/trips/hornbill/hornbill-morung.webp", webp_small: "/trips/hornbill/hornbill-morung-800.webp", alt: "Naga warriors dancing in front of a morung at the Hornbill Festival", focus: "55% 45%" },
    dance: { src: "/trips/hornbill/hornbill-dance.jpg", src_small: "/trips/hornbill/hornbill-dance-800.jpg", webp: "/trips/hornbill/hornbill-dance.webp", webp_small: "/trips/hornbill/hornbill-dance-800.webp", alt: "A Naga troupe in red shawls performing at the Kisama arena", focus: "50% 70%" },
};

export const HORNBILL = {
    slug: "hornbill-festival",
    name: "Hornbill Festival",
    year: 2026,
    start: "2026-12-01",
    end: "2026-12-10",
    datesLabel: "1 to 10 December 2026",
    venue: "Naga Heritage Village, Kisama",
    venueNote: "about 12 km south of Kohima",
    state: "Nagaland",
    gatewayName: "Dimapur",
    // Route search goes to Dimapur (airport and railway station), then road to Kohima
    gateway: { name: "Dimapur", search_name: "Dimapur, Nagaland", lat: 25.9063, lng: 93.7276, airport: "Dimapur Airport (DMU)", station: "Dimapur Railway Station (DMV)" },
    toKohima: "About 74 km, 2 to 3 hours by road. Shared taxis and buses leave from outside the station.",
    ilpUrl: "https://ilp.nagaland.gov.in/ilp/apply",
    officialUrl: "https://tourism.nagaland.gov.in",
    defaultDate: "2026-12-01",
    photo: "morung",
};

export const OPERATORS = {
    "innature-express": {
        slug: "innature-express", name: "InNature Express", tagline: "Explore, experience, connect",
        includes: ["stay", "breakfast", "dinner", "transport", "permits", "entry", "guide"],
        includesNote: "Accommodation, daily breakfast and dinner, transport from Dimapur, permits and entry fees, and an experienced tour guide.",
    },
    "yumrang-ways": {
        slug: "yumrang-ways", name: "Yumrang Ways", tagline: "When the forest tells the story",
        includesNote: "Stays with hygienic bathrooms, charging points, warm blankets and local support and guidance.",
    },
};

// What a package covers, for the comparison icons
export const INCLUDE_LABELS = {
    stay: "Stay", breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", transport: "Transport from Dimapur",
    permits: "Permits", entry: "Festival entry", guide: "Tour guide", blankets: "Warm blankets", support: "Local support",
};

const INN = "innature-express", YUM = "yumrang-ways";

export const PACKAGES = [
    {
        slug: "hornbill-weekender-kohima-khonoma", operator: INN, tier: "Package 1",
        name: "Hornbill Weekender: Kohima, Khonoma and Jakhama", nights: 2, days: 3, price: 7000, unit: "per person",
        places: ["Hornbill Festival", "Kohima", "Khonoma Village", "Jakhama Village"],
        includes: ["stay", "breakfast", "dinner", "transport", "permits", "entry", "guide"],
        blurb: "The shortest way to do Hornbill properly: festival days at Kisama, Kohima town, and the Angami villages of Khonoma and Jakhama.",
    },
    {
        slug: "hornbill-dzukou-valley", operator: INN, tier: "Package 2",
        name: "Hornbill and Dzükou Valley", nights: 3, days: 4, price: 9000, unit: "per person",
        places: ["Hornbill Festival", "Dzükou Valley", "Jakhama Village", "Kohima"],
        includes: ["stay", "breakfast", "dinner", "transport", "permits", "entry", "guide"],
        blurb: "Festival days plus the Dzükou Valley, the high grassland valley on the Nagaland and Manipur border, reached from Jakhama.",
    },
    {
        slug: "hornbill-kapamodzu-peak", operator: INN, tier: "Package 3",
        name: "Hornbill with Kapamodzü Peak Hike", nights: 3, days: 4, price: 10000, unit: "per person",
        places: ["Hornbill Festival", "Mt. Kapamodzü", "Kohima", "Khonoma Village", "Jakhama Village"],
        includes: ["stay", "breakfast", "dinner", "transport", "permits", "entry", "guide"],
        blurb: "Festival days, Khonoma and Jakhama, and a hike up Kapamodzü in Phek district, a 2,620 m table-top peak.",
    },
    {
        slug: "hornbill-highlands-kapamodzu", operator: INN, tier: "Package 5",
        name: "Hornbill Highlands: Kapamodzü, Khonoma and Jakhama", nights: 3, days: 4, price: 10499, unit: "per person",
        places: ["Hornbill Festival", "Mt. Kapamodzü", "Kohima", "Khonoma", "Jakhama Village"],
        includes: ["stay", "breakfast", "dinner", "transport", "permits", "entry", "guide"],
        blurb: "The same stops as the Kapamodzü Peak Hike package at a different price. Ask InNature Express what's different (stays or extras) before you pick.",
    },
    {
        slug: "hornbill-grand-dzukou-poliwa", operator: INN, tier: "Package 4",
        name: "Hornbill Grand Tour: Dzükou, Khonoma and Poliwa Sheep Farm", nights: 3, days: 4, price: 13500, unit: "per person",
        places: ["Hornbill Festival", "Dzükou Valley", "Khonoma Village", "Dziileke and Poliwa Sheep Farm", "Kohima", "Jakhama Village"],
        includes: ["stay", "breakfast", "dinner", "transport", "permits", "entry", "guide"],
        blurb: "The most places in four days: the festival, Dzükou Valley, Khonoma, Dziileke village and the Poliwa sheep farm, Kohima and Jakhama.",
    },
    {
        slug: "hornbill-festival-stay-basic", operator: YUM, tier: "Basic",
        name: "Hornbill Festival Stay, Basic", nights: null, days: null, price: 999, unit: "as listed",
        priceNote: "Price as listed by Yumrang Ways. Ask whether it is per person or per room, and for how many nights.",
        places: ["Hornbill Festival"],
        includes: ["stay", "breakfast", "blankets", "support"],
        blurb: "A simple stay for festival days: breakfast, hygienic bathroom, charging, warm blankets and local support. Bring your own plan for the rest.",
    },
    {
        slug: "hornbill-2-day-dimapur-standard", operator: YUM, tier: "Standard",
        name: "Hornbill 2-Day from Dimapur, Standard", nights: 1, days: 2, price: 2499, unit: "as listed",
        priceNote: "Price as listed by Yumrang Ways. Ask whether it is per person.",
        places: ["Hornbill Festival", "Dimapur"],
        includes: ["stay", "breakfast", "transport", "entry", "blankets", "support"],
        blurb: "Two days, one night: pickup and drop at Dimapur, a stay with breakfast, festival entry passes and local support.",
    },
    {
        slug: "hornbill-2-day-dimapur-all-meals", operator: YUM, tier: "Luxury",
        name: "Hornbill 2-Day from Dimapur, All Meals", nights: 1, days: 2, price: 2999, unit: "as listed",
        priceNote: "Price as listed by Yumrang Ways. Ask whether it is per person.",
        places: ["Hornbill Festival", "Dimapur"],
        includes: ["stay", "breakfast", "lunch", "dinner", "transport", "entry", "blankets", "support"],
        blurb: "Yumrang Ways' top tier: two days, one night, Dimapur pickup and drop, breakfast, lunch and dinner, and festival entry passes.",
    },
];

// "Packages under ₹X": cumulative price bands, each with its own page (/hornbill-festival/packages/under-<amount>)
export const BANDS = [
    { amount: 3000, label: "Under ₹3,000", hint: "Stays and short trips from Dimapur" },
    { amount: 10000, label: "Under ₹10,000", hint: "2 to 3 night trips with stays, meals and transport" },
    { amount: 15000, label: "Under ₹15,000", hint: "Everything here, up to 3 nights with hikes and villages" },
];

export const THINGS_TO_DO = [
    { name: "Hornbill Festival at Kisama", text: "Each Naga tribe has its own morung (traditional house) at the Heritage Village. Days are cultural performances, traditional games, food and craft stalls. Entry is a small fee at the gate." },
    { name: "Kohima War Cemetery", text: "The Second World War cemetery on Garrison Hill, where the 1944 Battle of Kohima was fought. Quiet, moving and right in town." },
    { name: "Evenings in Kohima town", text: "During festival week the town has evening events and food stalls. Kohima is where most visitors stay." },
    { name: "Khonoma village", text: "An Angami village about 20 km from Kohima, known for its terraced fields and community conservation work. Go with a local guide." },
    { name: "Dzükou Valley", text: "A high grassland valley on the Nagaland and Manipur border. The trail starts near Viswema or Jakhama; do it as a long day or stay overnight at the rest house." },
    { name: "Jakhama village", text: "A large Angami village on the Kohima to Imphal road, and the usual base for the Dzükou trek." },
    { name: "Kapamodzü peak", text: "A 2,620 m table-top mountain at Zhavame in Phek district, the fourth highest in Nagaland. Popular for sunrise hikes; it is a long drive from Kohima, so plan a day for it." },
];

export const FAQS = [
    { q: "When is the Hornbill Festival 2026?", a: "1 to 10 December 2026. It starts on Nagaland's Statehood Day, 1 December, every year. Check the official site for the day by day programme." },
    { q: "Where is the Hornbill Festival held?", a: "At the Naga Heritage Village in Kisama, about 12 km south of Kohima, the capital of Nagaland. Most visitors stay in Kohima or nearby villages and drive in each day." },
    { q: "How do I reach the Hornbill Festival?", a: "Get to Dimapur first: it has the nearest airport and the main railway station. From Dimapur it is about 74 km to Kohima, 2 to 3 hours by road, by shared taxi, bus or the operator's cab. Use the search on this page for trains, flights and buses from your city to Dimapur." },
    { q: "Do I need a permit to visit Nagaland for Hornbill?", a: "Indian visitors from outside Nagaland need an Inner Line Permit (ILP). Apply online on the Nagaland ILP portal before you travel. Many packages on this page include permits; confirm with the operator. Foreign nationals follow separate registration rules." },
    { q: "How much does a Hornbill Festival package cost?", a: "On this page, from ₹999 for a basic stay (Yumrang Ways) to ₹13,500 per person for a 3 night, 4 day tour with Dzükou Valley, Khonoma and the Poliwa sheep farm (InNature Express). What's included differs a lot, so compare the inclusions, not just the price." },
    { q: "Is there an entry ticket for the Hornbill Festival?", a: "Yes, a small entry fee paid at the gate. Some packages include festival entry." },
    { q: "Should I book early for Hornbill?", a: "Yes. Stays in and around Kohima fill up during festival week, and trains and flights to Dimapur get busy around 1 December. Ask operators for their best price early and keep your ILP ready." },
    { q: "What is the weather like in December?", a: "Days are cool and nights are cold in Kohima in December. Pack warm layers, a jacket and good walking shoes." },
];

export const pkgPath = (p) => `/hornbill-festival/package/${p.slug}`;
// Packages alternate between the two photos (cheapest first), unless one sets its own
export const photoOf = (p) => PHOTOS[p?.photo] || PHOTOS[[...PACKAGES].sort((a, b) => a.price - b.price).findIndex((x) => x.slug === p?.slug) % 2 ? "morung" : "dance"] || PHOTOS.morung;
export const bandPath = (b) => `/hornbill-festival/packages/under-${b.amount}`;
export const operatorOf = (p) => OPERATORS[p.operator] || { name: p.operator };
export const findPackage = (slug) => PACKAGES.find((p) => p.slug === slug) || null;
export const findBand = (amount) => BANDS.find((b) => String(b.amount) === String(amount)) || null;
export const packagesUnder = (amount) => PACKAGES.filter((p) => p.price <= amount).sort((a, b) => a.price - b.price);
export const durationLabel = (p) => (p.nights == null ? "Stay" : `${p.nights}N/${p.days}D`);
export const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
export const minPrice = () => Math.min(...PACKAGES.map((p) => p.price));
export const maxPrice = () => Math.max(...PACKAGES.map((p) => p.price));

// Packages in the shape the hub's storefront and sitemap use (kind "package"), for pages that list
// partners and their trips when the hub isn't reachable
export const hornbillPartnerTrips = (partnerSlug = "") => PACKAGES.filter((p) => !partnerSlug || p.operator === partnerSlug).map((p) => ({
    slug: p.slug, kind: "package", festival: "hornbill-2026", title: p.name, nights: p.nights, price_pp: p.price, page_path: pkgPath(p),
    partner: { slug: p.operator, name: operatorOf(p).name, short_name: operatorOf(p).name, tagline: operatorOf(p).tagline },
}));

// The hub's minimal trip for chat (useTravellerChat / conv_start): the hub fills the operator from its own row
export const chatTrip = (p) => ({ slug: p.slug, title: p.name, partner: { slug: p.operator, name: operatorOf(p).name, short_name: operatorOf(p).name } });
